import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  KeyRound,
  Plus,
  AlertTriangle,
  Clock3,
  CheckCircle2,
  Printer,
  User,
  Phone,
  Calendar,
  Car,
  Trash2,
} from "lucide-react";
import { api, apiErrorMessage } from "../lib/api";
import { currencyTRY, dateTR, daysBetween, today, numberTR } from "../lib/format";
import { ODEME_YONTEMI } from "../lib/constants";
import { useAuth } from "../lib/auth";
import { whatsappUrl } from "../components/Reminders";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Skeleton } from "../components/ui/skeleton";

function OdemeBadge({ durum }) {
  const map = {
    Ödendi: "bg-emerald-100 text-emerald-800",
    "Kısmi Ödeme": "bg-amber-100 text-amber-800",
    Ödenmedi: "bg-rose-100 text-rose-800",
  };
  return (
    <span
      className={`inline-flex px-2.5 py-1 rounded-full text-[11px] font-semibold ${
        map[durum] || "bg-stone-100 text-stone-700"
      }`}
    >
      {durum}
    </span>
  );
}

function statusOf(r) {
  if (r.durum === "Tamamlandı") return "completed";
  const today0 = new Date().toISOString().slice(0, 10);
  if (r.bitisTarihi < today0) return "overdue";
  if (r.bitisTarihi === today0) return "today";
  return "active";
}

function RentalCard({ r, onClick }) {
  const s = statusOf(r);
  const border = {
    overdue: "border-rose-300 bg-rose-50/40",
    today: "border-amber-300 bg-amber-50/40",
    active: "border-stone-200 bg-white",
    completed: "border-stone-200 bg-white",
  }[s];
  const label = {
    overdue: (
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-rose-800 bg-rose-100 rounded-full px-2 py-0.5">
        <AlertTriangle className="w-3 h-3" /> Gecikmiş
      </span>
    ),
    today: (
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-800 bg-amber-100 rounded-full px-2 py-0.5">
        <Clock3 className="w-3 h-3" /> Bugün İade
      </span>
    ),
    active: (
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-800 bg-emerald-100 rounded-full px-2 py-0.5">
        Devam Ediyor
      </span>
    ),
    completed: (
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-stone-700 bg-stone-200 rounded-full px-2 py-0.5">
        <CheckCircle2 className="w-3 h-3" /> Tamamlandı
      </span>
    ),
  }[s];

  return (
    <button
      onClick={onClick}
      data-testid={`rent-card-${r.id}`}
      className={`text-left rounded-2xl border p-4 hover:-translate-y-0.5 transition-all ${border}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-display font-bold text-slate-900">
            {r.aracMarka} {r.aracModel}
          </div>
          <div className="text-xs text-stone-500 mt-0.5">{r.plaka}</div>
        </div>
        {label}
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
        <div className="flex items-center gap-1.5 text-stone-600">
          <User className="w-3.5 h-3.5" />
          <span className="truncate">{r.musteriAdi}</span>
        </div>
        <div className="flex items-center gap-1.5 text-stone-600">
          <Phone className="w-3.5 h-3.5" />
          <span className="truncate">{r.musteriTelefon}</span>
        </div>
      </div>
      <div className="mt-2 text-xs text-stone-500 flex items-center gap-1.5">
        <Calendar className="w-3.5 h-3.5" />
        {dateTR(r.baslangicTarihi)} → {dateTR(r.bitisTarihi)} · {r.gun} gün
      </div>
      <div className="mt-3 flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-stone-400">
            Toplam
          </div>
          <div className="font-display font-black text-slate-900 num-tabular">
            {currencyTRY(r.toplamTutar)}
          </div>
        </div>
        <OdemeBadge durum={r.odemeDurumu} />
      </div>
    </button>
  );
}

function RentalForm({ open, initial, onClose, onSaved }) {
  const [form, setForm] = useState(
    initial || {
      plaka: "",
      aracMarka: "",
      aracModel: "",
      teslimKm: 0,
      musteriAdi: "",
      musteriTelefon: "",
      musteriTC: "",
      baslangicTarihi: today(),
      bitisTarihi: today(),
      gunlukUcret: 1500,
      depozitoTutari: 3000,
      depozitoAlindi: true,
      depozitoIadeEdildi: false,
      odenenTutar: 0,
      odemeYontemi: "Nakit",
      notlar: "",
    },
  );
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const gun = Math.max(daysBetween(form.baslangicTarihi, form.bitisTarihi), 1);
  const toplam = gun * Number(form.gunlukUcret || 0);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        teslimKm: Number(form.teslimKm),
        gunlukUcret: Number(form.gunlukUcret),
        depozitoTutari: Number(form.depozitoTutari),
        odenenTutar: Number(form.odenenTutar),
      };
      if (initial?.id) {
        await api.put(`/rentals/${initial.id}`, payload);
      } else {
        await api.post("/rentals", payload);
      }
      toast.success("Kiralama kaydedildi");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  const inputCls =
    "w-full h-10 px-3 rounded-xl border border-stone-300 text-sm focus:border-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-600/20";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">
            {initial ? "Kiralama Düzenle" : "Yeni Kiralama"}
          </DialogTitle>
          <DialogDescription>
            Araç ve müşteri bilgileriyle beraber ödeme detaylarını girin.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-stone-600">
                Plaka
              </label>
              <input
                required
                data-testid="rent-plaka"
                value={form.plaka}
                onChange={(e) => set("plaka", e.target.value.toUpperCase())}
                className={inputCls + " mt-1"}
              />
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-stone-600">
                Araç Marka
              </label>
              <input
                required
                data-testid="rent-marka"
                value={form.aracMarka}
                onChange={(e) => set("aracMarka", e.target.value)}
                className={inputCls + " mt-1"}
              />
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-stone-600">
                Araç Model
              </label>
              <input
                required
                data-testid="rent-model"
                value={form.aracModel}
                onChange={(e) => set("aracModel", e.target.value)}
                className={inputCls + " mt-1"}
              />
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-stone-600">
                Teslim Km
              </label>
              <input
                type="number"
                value={form.teslimKm}
                onChange={(e) => set("teslimKm", e.target.value)}
                className={inputCls + " mt-1"}
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-[11px] font-semibold uppercase tracking-wider text-stone-600">
                Müşteri Adı
              </label>
              <input
                required
                data-testid="rent-musteri"
                value={form.musteriAdi}
                onChange={(e) => set("musteriAdi", e.target.value)}
                className={inputCls + " mt-1"}
              />
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-stone-600">
                Telefon
              </label>
              <input
                value={form.musteriTelefon}
                onChange={(e) => set("musteriTelefon", e.target.value)}
                className={inputCls + " mt-1"}
              />
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-stone-600">
                TC Kimlik
              </label>
              <input
                value={form.musteriTC}
                onChange={(e) => set("musteriTC", e.target.value)}
                className={inputCls + " mt-1"}
              />
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-stone-600">
                Başlangıç
              </label>
              <input
                type="date"
                required
                data-testid="rent-baslangic"
                value={form.baslangicTarihi}
                onChange={(e) => set("baslangicTarihi", e.target.value)}
                className={inputCls + " mt-1"}
              />
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-stone-600">
                Bitiş
              </label>
              <input
                type="date"
                required
                data-testid="rent-bitis"
                value={form.bitisTarihi}
                onChange={(e) => set("bitisTarihi", e.target.value)}
                className={inputCls + " mt-1"}
              />
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-stone-600">
                Günlük Ücret (₺)
              </label>
              <input
                type="number"
                required
                data-testid="rent-gunluk"
                value={form.gunlukUcret}
                onChange={(e) => set("gunlukUcret", e.target.value)}
                className={inputCls + " mt-1"}
              />
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-stone-600">
                Depozito (₺)
              </label>
              <input
                type="number"
                value={form.depozitoTutari}
                onChange={(e) => set("depozitoTutari", e.target.value)}
                className={inputCls + " mt-1"}
              />
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-stone-600">
                Ödenen (₺)
              </label>
              <input
                type="number"
                data-testid="rent-odenen"
                value={form.odenenTutar}
                onChange={(e) => set("odenenTutar", e.target.value)}
                className={inputCls + " mt-1"}
              />
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-stone-600">
                Ödeme Yöntemi
              </label>
              <select
                value={form.odemeYontemi}
                onChange={(e) => set("odemeYontemi", e.target.value)}
                className={inputCls + " mt-1"}
              >
                {ODEME_YONTEMI.map((o) => (
                  <option key={o}>{o}</option>
                ))}
              </select>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-stone-700">
            <input
              type="checkbox"
              checked={form.depozitoAlindi}
              onChange={(e) => set("depozitoAlindi", e.target.checked)}
            />
            Depozito alındı
          </label>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-stone-600">
              Notlar
            </label>
            <textarea
              rows={2}
              value={form.notlar}
              onChange={(e) => set("notlar", e.target.value)}
              className="mt-1 w-full px-3 py-2 rounded-xl border border-stone-300 text-sm"
            />
          </div>

          <div className="rounded-xl bg-stone-50 p-3 flex items-center justify-between">
            <span className="text-sm text-stone-600">
              {gun} gün × {currencyTRY(form.gunlukUcret)}
            </span>
            <span className="font-display font-black text-xl text-slate-900 num-tabular">
              {currencyTRY(toplam)}
            </span>
          </div>

          <DialogFooter>
            <button
              type="submit"
              data-testid="rent-submit"
              className="h-11 px-6 rounded-full bg-orange-700 hover:bg-orange-800 text-white font-semibold text-sm"
            >
              Kaydet
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function RentalDetail({ rental, onClose, onUpdate }) {
  const { user } = useAuth();
  const [returnOpen, setReturnOpen] = useState(false);
  const [rf, setRf] = useState({
    iadeTarihi: today(),
    iadeKm: rental.teslimKm || 0,
    odenenTutar: rental.odenenTutar || rental.toplamTutar,
    depozitoIadeEdildi: true,
    notlar: "",
  });

  const kalan = Math.max(rental.toplamTutar - (rental.odenenTutar || 0), 0);

  const submitReturn = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/rentals/${rental.id}/return`, {
        ...rf,
        iadeKm: Number(rf.iadeKm),
        odenenTutar: Number(rf.odenenTutar),
      });
      toast.success("Kiralama tamamlandı");
      setReturnOpen(false);
      onUpdate();
      onClose();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  const remove = async () => {
    if (!window.confirm("Kiralama kaydını silmek istediğinize emin misiniz?"))
      return;
    try {
      await api.delete(`/rentals/${rental.id}`);
      toast.success("Silindi");
      onUpdate();
      onClose();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  const printContract = () => {
    const w = window.open("", "_blank", "width=800,height=900");
    if (!w) return;
    w.document.write(`
      <html><head><title>Kira Sözleşmesi - ${rental.plaka}</title>
      <style>
        body{font-family:Karla,Arial,sans-serif;padding:40px;color:#1c1917;line-height:1.6}
        h1{font-family:Chivo,Arial,sans-serif;font-weight:900;font-size:28px;margin:0 0 4px}
        .sub{color:#78716c;text-transform:uppercase;letter-spacing:0.15em;font-size:11px;font-weight:600}
        .box{border:1px solid #e7e5e4;border-radius:12px;padding:16px;margin-top:16px}
        .grid{display:grid;grid-template-columns:1fr 1fr;gap:12px}
        .k{font-size:11px;color:#78716c;text-transform:uppercase;letter-spacing:0.1em}
        .v{font-weight:600;color:#1e293b}
        .total{background:#1e293b;color:white;padding:16px;border-radius:12px;margin-top:16px;display:flex;justify-content:space-between;align-items:center}
        .sig{display:flex;justify-content:space-between;margin-top:60px;gap:40px}
        .sig div{flex:1;border-top:1px solid #78716c;padding-top:8px;font-size:12px;color:#57534e}
        p{margin:8px 0}
      </style></head><body>
      <div class="sub">Kira Sözleşmesi Özeti</div>
      <h1>Oto Galeri & Kiralama</h1>
      <div class="box">
        <div class="grid">
          <div><div class="k">Araç</div><div class="v">${rental.aracMarka} ${rental.aracModel}</div></div>
          <div><div class="k">Plaka</div><div class="v">${rental.plaka}</div></div>
          <div><div class="k">Müşteri</div><div class="v">${rental.musteriAdi}</div></div>
          <div><div class="k">TC / Telefon</div><div class="v">${rental.musteriTC || "-"} · ${rental.musteriTelefon || "-"}</div></div>
          <div><div class="k">Başlangıç</div><div class="v">${dateTR(rental.baslangicTarihi)}</div></div>
          <div><div class="k">Bitiş</div><div class="v">${dateTR(rental.bitisTarihi)}</div></div>
          <div><div class="k">Gün</div><div class="v">${rental.gun}</div></div>
          <div><div class="k">Günlük Ücret</div><div class="v">${currencyTRY(rental.gunlukUcret)}</div></div>
        </div>
      </div>
      <div class="total"><span>Toplam Kira Bedeli</span><b>${currencyTRY(rental.toplamTutar)}</b></div>
      <p style="margin-top:24px;font-size:13px;color:#57534e">Kiracı, aracı belirtilen tarihler arasında kullanmak üzere teslim aldığını, kira bedelini ödediğini, aracı hasarsız iade edeceğini, tüm trafik cezası ve masraflarının kendisine ait olduğunu beyan ve kabul eder.</p>
      <div class="sig"><div>Kiralayan (Galeri)</div><div>Kiracı</div></div>
      <script>window.print()</script>
      </body></html>
    `);
    w.document.close();
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {rental.aracMarka} {rental.aracModel}
          </DialogTitle>
          <DialogDescription>
            {rental.plaka} · Kiracı: {rental.musteriAdi}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="rounded-xl bg-stone-50 p-3">
            <div className="text-[10px] uppercase tracking-widest text-stone-500">
              Başlangıç
            </div>
            <div className="font-semibold text-slate-900">
              {dateTR(rental.baslangicTarihi)}
            </div>
          </div>
          <div className="rounded-xl bg-stone-50 p-3">
            <div className="text-[10px] uppercase tracking-widest text-stone-500">
              Bitiş
            </div>
            <div className="font-semibold text-slate-900">
              {dateTR(rental.bitisTarihi)}
            </div>
          </div>
          <div className="rounded-xl bg-stone-50 p-3">
            <div className="text-[10px] uppercase tracking-widest text-stone-500">
              Gün
            </div>
            <div className="font-display font-black text-slate-900 num-tabular">
              {rental.gun}
            </div>
          </div>
          <div className="rounded-xl bg-stone-50 p-3">
            <div className="text-[10px] uppercase tracking-widest text-stone-500">
              Günlük
            </div>
            <div className="font-display font-black text-slate-900 num-tabular">
              {currencyTRY(rental.gunlukUcret)}
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-stone-200 p-4 mt-3">
          <div className="flex items-center justify-between">
            <span className="text-stone-500">Toplam</span>
            <span className="font-display font-black text-2xl text-slate-900 num-tabular">
              {currencyTRY(rental.toplamTutar)}
            </span>
          </div>
          <div className="flex items-center justify-between mt-2">
            <span className="text-stone-500">Ödenen</span>
            <span className="font-semibold text-emerald-700 num-tabular">
              {currencyTRY(rental.odenenTutar)}
            </span>
          </div>
          <div className="flex items-center justify-between mt-1">
            <span className="text-stone-500">Kalan</span>
            <span className="font-semibold text-rose-700 num-tabular">
              {currencyTRY(kalan)}
            </span>
          </div>
          <div className="flex items-center justify-between mt-2">
            <span className="text-stone-500">Depozito</span>
            <span className="text-slate-800 num-tabular">
              {currencyTRY(rental.depozitoTutari)} ·{" "}
              {rental.depozitoAlindi ? "Alındı" : "Alınmadı"}
              {rental.depozitoIadeEdildi ? " · İade edildi" : ""}
            </span>
          </div>
        </div>

        {rental.durum === "Tamamlandı" && (
          <div className="rounded-xl bg-emerald-50/60 border border-emerald-200 p-3 mt-3 text-sm text-emerald-900">
            <b>Tamamlandı</b> · İade: {dateTR(rental.iadeTarihi)} ·{" "}
            {numberTR(rental.iadeKm)} km
          </div>
        )}

        {rental.notlar && (
          <div className="rounded-xl border border-stone-200 p-3 mt-3 text-sm text-stone-700 whitespace-pre-line">
            <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">
              Notlar
            </div>
            {rental.notlar}
          </div>
        )}

        <DialogFooter className="gap-2 flex-wrap">
          {rental.musteriTelefon && rental.durum === "Devam Ediyor" && (
            <a
              href={whatsappUrl(
                rental.musteriTelefon,
                `Merhaba ${rental.musteriAdi}, ${rental.aracMarka} ${rental.aracModel} (${rental.plaka}) kiralamanızın iade tarihi ${dateTR(rental.bitisTarihi)}. İletişim için yazabilirsiniz. Teşekkürler — Oto Galeri`,
              )}
              target="_blank"
              rel="noreferrer"
              data-testid="rent-wa"
              className="h-11 px-4 rounded-full bg-[#25D366] hover:bg-[#20BD5B] text-white font-semibold text-sm flex items-center gap-2"
            >
              <Printer className="w-4 h-4 hidden" />
              WhatsApp
            </a>
          )}
          <button
            onClick={printContract}
            data-testid="rent-print"
            className="h-11 px-4 rounded-full border border-stone-300 text-slate-800 hover:bg-stone-50 font-semibold text-sm flex items-center gap-2"
          >
            <Printer className="w-4 h-4" /> Sözleşme Yazdır
          </button>
          {user?.role === "admin" && (
            <button
              onClick={remove}
              data-testid="rent-delete"
              className="h-11 px-4 rounded-full border border-rose-200 text-rose-700 hover:bg-rose-50 font-semibold text-sm flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" /> Sil
            </button>
          )}
          {rental.durum === "Devam Ediyor" && (
            <button
              onClick={() => setReturnOpen(true)}
              data-testid="rent-iade"
              className="h-11 px-6 rounded-full bg-orange-700 hover:bg-orange-800 text-white font-semibold text-sm"
            >
              İade Al
            </button>
          )}
        </DialogFooter>

        <Dialog open={returnOpen} onOpenChange={setReturnOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display">İade Al</DialogTitle>
              <DialogDescription>
                Aracın iade bilgilerini ve kalan ödemeyi giriniz.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submitReturn} className="space-y-3">
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-stone-600">
                  İade Tarihi
                </label>
                <input
                  type="date"
                  required
                  data-testid="iade-tarih"
                  value={rf.iadeTarihi}
                  onChange={(e) =>
                    setRf({ ...rf, iadeTarihi: e.target.value })
                  }
                  className="mt-1 w-full h-11 px-3 rounded-xl border border-stone-300"
                />
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-stone-600">
                  İade Km
                </label>
                <input
                  type="number"
                  required
                  data-testid="iade-km"
                  value={rf.iadeKm}
                  onChange={(e) => setRf({ ...rf, iadeKm: e.target.value })}
                  className="mt-1 w-full h-11 px-3 rounded-xl border border-stone-300"
                />
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-stone-600">
                  Tahsil Edilen Toplam (₺)
                </label>
                <input
                  type="number"
                  required
                  data-testid="iade-odenen"
                  value={rf.odenenTutar}
                  onChange={(e) =>
                    setRf({ ...rf, odenenTutar: e.target.value })
                  }
                  className="mt-1 w-full h-11 px-3 rounded-xl border border-stone-300"
                />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={rf.depozitoIadeEdildi}
                  onChange={(e) =>
                    setRf({ ...rf, depozitoIadeEdildi: e.target.checked })
                  }
                />
                Depozito iade edildi
              </label>
              <textarea
                rows={2}
                placeholder="İade notu (opsiyonel)"
                value={rf.notlar}
                onChange={(e) => setRf({ ...rf, notlar: e.target.value })}
                className="w-full px-3 py-2 rounded-xl border border-stone-300 text-sm"
              />
              <DialogFooter>
                <button
                  type="submit"
                  data-testid="iade-submit"
                  className="h-11 px-6 rounded-full bg-orange-700 hover:bg-orange-800 text-white font-semibold text-sm"
                >
                  İadeyi Tamamla
                </button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}

export default function Rentals() {
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    try {
      const res = await api.get("/rentals");
      setRentals(res.data);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const groups = useMemo(() => {
    const g = { active: [], overdue: [], todayR: [], completed: [] };
    for (const r of rentals) {
      const s = statusOf(r);
      if (s === "overdue") g.overdue.push(r);
      else if (s === "today") g.todayR.push(r);
      else if (s === "completed") g.completed.push(r);
      else g.active.push(r);
    }
    return g;
  }, [rentals]);

  const counters = [
    { key: "active", label: "Aktif", value: groups.active.length, tone: "bg-emerald-50 text-emerald-800 border-emerald-200" },
    { key: "overdue", label: "Gecikmiş", value: groups.overdue.length, tone: "bg-rose-50 text-rose-800 border-rose-200" },
    { key: "today", label: "Bugün İade", value: groups.todayR.length, tone: "bg-amber-50 text-amber-800 border-amber-200" },
    { key: "completed", label: "Tamamlanan", value: groups.completed.length, tone: "bg-stone-50 text-stone-700 border-stone-200" },
  ];

  return (
    <div data-testid="rentals-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-stone-500">
            Kiralama
          </div>
          <h1 className="font-display font-black text-3xl md:text-4xl text-slate-900 mt-1">
            Filo Kiralamaları
          </h1>
        </div>
        <button
          onClick={() => setFormOpen(true)}
          data-testid="rent-add"
          className="h-11 px-5 rounded-full bg-orange-700 hover:bg-orange-800 text-white font-semibold text-sm flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Yeni Kiralama
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {counters.map((c) => (
          <div
            key={c.key}
            className={`rounded-2xl border p-4 ${c.tone}`}
            data-testid={`rent-counter-${c.key}`}
          >
            <div className="text-[10px] uppercase tracking-widest opacity-80">
              {c.label}
            </div>
            <div className="font-display font-black text-3xl num-tabular mt-1">
              {c.value}
            </div>
          </div>
        ))}
      </div>

      <Tabs defaultValue="active">
        <TabsList className="bg-stone-100 rounded-full p-1 mb-5">
          <TabsTrigger value="active" data-testid="rent-tab-active" className="rounded-full">Aktif</TabsTrigger>
          <TabsTrigger value="overdue" data-testid="rent-tab-overdue" className="rounded-full">Gecikmiş</TabsTrigger>
          <TabsTrigger value="today" data-testid="rent-tab-today" className="rounded-full">Bugün</TabsTrigger>
          <TabsTrigger value="completed" data-testid="rent-tab-completed" className="rounded-full">Tamamlanan</TabsTrigger>
        </TabsList>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-40 rounded-2xl" />
            ))}
          </div>
        ) : (
          <>
            {[
              ["active", groups.active, "Aktif kiralama yok."],
              ["overdue", groups.overdue, "Gecikmiş kiralama yok."],
              ["today", groups.todayR, "Bugün iade edilecek kiralama yok."],
              ["completed", groups.completed, "Henüz tamamlanan kiralama yok."],
            ].map(([key, list, empty]) => (
              <TabsContent key={key} value={key}>
                {list.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-stone-300 bg-white/40 p-12 text-center text-stone-500">
                    {empty}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {list.map((r) => (
                      <RentalCard
                        key={r.id}
                        r={r}
                        onClick={() => setSelected(r)}
                      />
                    ))}
                  </div>
                )}
              </TabsContent>
            ))}
          </>
        )}
      </Tabs>

      {formOpen && (
        <RentalForm
          open
          onClose={() => setFormOpen(false)}
          onSaved={load}
        />
      )}
      {selected && (
        <RentalDetail
          rental={rentals.find((r) => r.id === selected.id) || selected}
          onClose={() => setSelected(null)}
          onUpdate={load}
        />
      )}
    </div>
  );
}
